import { FC } from 'react';
import {
  LeaderboardDesktop,
  LeaderboardMobile,
} from '@wwe-portal/ui/features/leaderboard/leaderboard';
import {
  CollapsableContent,
  LeaderboardRowDesktop,
  LeaderboardRowDesktopLayout,
  LeaderboardRowMobile,
} from '@wwe-portal/ui/features/leaderboard/leaderboard-row';
import { FactionLink } from '../common/link/faction-link';
import { useTranslations } from 'next-intl';
import { PlayerNameCell } from './player-name-cell';

import EmptyLeaderboardBody from './empty-leaderboard-body';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@wwe-portal/ui/components/arccordion';
import { PlayerLeaderboardProps } from './player-leaderboard.type';
import { FactionRankData } from './faction-leaderboard.type';
import { cn } from '@wwe-portal/ui/lib/lib';

export const playerTableHeader = {
  rank: 'RANK',
  sub: 'FACTION',
  main: {
    short: 'PLAYER & FACTION',
    long: 'PLAYER',
  },
  point: 'POINTS',
};

export const FactionName: FC<{ data: FactionRankData; className?: string }> = ({
  data: { faction },
  className,
}) => {
  const t = useTranslations();
  return (
    <FactionLink
      className={cn(
        'text-text500 hover:text-text300 text-2xs truncate no-underline hover:no-underline md:text-xs',
        className
      )}
      name={faction}
    >
      {faction ?? t('no_faction')}
    </FactionLink>
  );
};

export const PlayerLeaderboardMobile = ({
  data,
  rowHeader,
  mobileHeader,
  hideAvatar,
  currentUserId,
  rowClassName,
  containerClassnames,
  customColumnNames,
}: Pick<
  PlayerLeaderboardProps,
  | 'data'
  | 'rowHeader'
  | 'mobileHeader'
  | 'hideAvatar'
  | 'currentUserId'
  | 'rowClassName'
  | 'containerClassnames'
  | 'customColumnNames'
>) => {
  const isEmpty = data.length === 0;
  return (
    <LeaderboardMobile
      className={containerClassnames}
      header={mobileHeader}
      rows={
        isEmpty ? (
          <EmptyLeaderboardBody />
        ) : (
          <>
            {rowHeader}
            <Accordion type="single" collapsible>
              {data.map((item, index) => (
                <AccordionItem
                  value={index.toString()}
                  key={index}
                  data-me={item.userId === currentUserId}
                  className={cn(
                    'data-[me=true]:bg-borderColor7 data-[me=true]:border-borderColor15 rounded-xl odd:bg-[#3d426e] data-[me=true]:border',
                    {
                      'border-back200 rounded-xl border border-solid':
                        item.rank === 1,
                    },
                    rowClassName
                  )}
                >
                  <AccordionTrigger>
                    <LeaderboardRowMobile
                      data={item}
                      isCurrentUser={item.userId === currentUserId}
                      title={
                        <PlayerNameCell
                          hideAvatar={hideAvatar}
                          data={{
                            name: item.name,
                            faction: item.faction,
                            userId: item.userId,
                          }}
                        />
                      }
                      subtitle={<FactionName data={item} />}
                    />
                  </AccordionTrigger>
                  <AccordionContent>
                    <CollapsableContent
                      points={item.points}
                      faction={item.faction}
                      endDate={item.endDate}
                      expanded
                      customColumnNames={customColumnNames}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </>
        )
      }
    />
  );
};

export const PlayerLeaderboardHeadTitleCell: FC<{
  compact: boolean;
  main: {
    short: string;
    long: string;
  };
}> = ({ compact, main }) => {
  return compact ? (
    <>
      <span className="3xl:hidden hidden 2xl:block">{main.short}</span>
      <span className="3xl:block 2xl:hidden">{main.long}</span>
    </>
  ) : (
    <span>{playerTableHeader.main.long}</span>
  );
};

export const PlayerLeaderboardDesktop = ({
  data,
  desktopHeader,
  rowClassNames,
  rowClassName,
  classNames,
  containerClassnames,
  desktopRowHeader,
  currentUserId,
  compact,
}: Pick<
  PlayerLeaderboardProps,
  | 'data'
  | 'desktopHeader'
  | 'rowClassNames'
  | 'rowClassName'
  | 'classNames'
  | 'containerClassnames'
  | 'desktopRowHeader'
  | 'currentUserId'
  | 'compact'
>) => {
  const isEmpty = data.length === 0;
  return (
    <LeaderboardDesktop
      className={containerClassnames}
      header={desktopHeader}
      rows={
        isEmpty ? (
          <EmptyLeaderboardBody />
        ) : (
          <>
            {desktopRowHeader ?? (
              <LeaderboardRowDesktopLayout
                compact={compact}
                rowClassNames={rowClassNames}
                className="text-text700 text-xs font-semibold"
                points={playerTableHeader.point}
                rank={playerTableHeader.rank}
                subtitle={playerTableHeader.sub}
                title={
                  <PlayerLeaderboardHeadTitleCell
                    compact={compact}
                    main={playerTableHeader.main}
                  />
                }
              />
            )}
            {data.map((item, index) => (
              <div
                className={cn('even:bg-back400 rounded-xl', rowClassName)}
                data-me={item.userId === currentUserId}
                key={index}
              >
                <LeaderboardRowDesktop
                  compact={compact}
                  className={classNames}
                  rowClassNames={rowClassNames}
                  data={item}
                  title={<PlayerNameCell compact={compact} data={item} />}
                  subtitle={
                    <FactionName
                      data={item}
                      className="text-text300 hover:text-white"
                    />
                  }
                  isCurrentUser={currentUserId === item.userId}
                />
              </div>
            ))}
          </>
        )
      }
    />
  );
};
